import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Download, Loader2, X } from "lucide-react";
import { useState } from "react";

import {
  crearInvite,
  descargarArchivoRegistro,
  getSubmission,
  listarInvites,
  listarSubmissions,
  revocarInvite,
  type RegistroInvite,
  type RegistroInviteCreated,
  type RegistroSubmissionDetail,
} from "@/lib/api";

const STATUS_BADGE: Record<RegistroInvite["status"], { label: string; cls: string }> = {
  valid: { label: "Disponible", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  used: { label: "Enviada", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  expired: { label: "Expirada", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  revoked: { label: "Cancelada", cls: "bg-red-50 text-red-600 border-red-200" },
  not_found: { label: "—", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
}

export function RegistroProveedoresPage() {
  const qc = useQueryClient();
  const invitesQ = useQuery({ queryKey: ["registro", "invites"], queryFn: listarInvites });
  const subsQ = useQuery({ queryKey: ["registro", "submissions"], queryFn: listarSubmissions });

  const [email, setEmail] = useState("");
  const [etiqueta, setEtiqueta] = useState("");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<RegistroInviteCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [detail, setDetail] = useState<RegistroSubmissionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function generar(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setCreated(null);
    setCreating(true);
    try {
      const res = await crearInvite(email.trim(), etiqueta.trim());
      setCreated(res);
      setEmail("");
      setEtiqueta("");
      qc.invalidateQueries({ queryKey: ["registro", "invites"] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function copiar(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard bloqueado */
    }
  }

  async function revocar(id: number) {
    if (!confirm("¿Cancelar esta invitación? El enlace dejará de funcionar.")) return;
    try {
      await revocarInvite(id);
      qc.invalidateQueries({ queryKey: ["registro", "invites"] });
    } catch (err) {
      alert((err as Error).message);
    }
  }

  async function verDetalle(id: number) {
    setDetailLoading(true);
    try {
      setDetail(await getSubmission(id));
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-slate-800">Registro de proveedores</h1>
        <p className="text-sm text-slate-500">
          Genera un enlace de un solo uso para que un proveedor capture sus datos de alta en
          www.mppenergia.com/registro. El enlace se envía por correo y pide un código de
          verificación antes de dejar capturar.
        </p>
      </div>

      {/* Generar invitación */}
      <div className="bg-white border rounded-lg p-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Generar invitación</h2>
        <form onSubmit={generar} className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Correo del proveedor *</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contacto@proveedor.com"
              className="mt-1 border rounded px-2 py-1.5 text-sm block w-72"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Etiqueta / nota (opcional)</span>
            <input
              type="text"
              value={etiqueta}
              onChange={(e) => setEtiqueta(e.target.value)}
              placeholder="Nombre del proveedor"
              className="mt-1 border rounded px-2 py-1.5 text-sm block w-72"
            />
          </label>
          <button
            type="submit"
            disabled={creating}
            className="inline-flex items-center gap-1.5 text-sm bg-slate-800 text-white rounded px-3 py-2 hover:bg-slate-700 disabled:opacity-50"
          >
            {creating && <Loader2 className="w-4 h-4 animate-spin" />}
            Generar enlace
          </button>
        </form>

        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}

        {created && (
          <div className="mt-4 border border-emerald-200 bg-emerald-50 rounded p-3">
            <div className="text-xs text-emerald-700 font-medium mb-1">
              Enlace generado para {created.email} · vence {fmtDate(created.expira_en)}
            </div>
            <div className={`text-[11px] mb-2 ${created.correo_enviado ? "text-emerald-700/80" : "text-red-600"}`}>
              {created.correo_enviado
                ? `Se envió por correo a ${created.email} con las instrucciones para completarlo. También puedes copiarlo aquí (solo se muestra una vez).`
                : `${created.correo_detalle ?? "No se pudo enviar el correo."} Cópialo y envíaselo al proveedor (solo se muestra una vez).`}
            </div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={created.link}
                className="flex-1 border rounded px-2 py-1.5 text-xs font-mono bg-white"
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                onClick={() => copiar(created.link)}
                className="inline-flex items-center gap-1.5 text-sm border rounded px-3 py-1.5 bg-white hover:bg-slate-50"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copiado" : "Copiar"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Invitaciones */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b text-sm font-semibold text-slate-700">Invitaciones</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Correo</th>
              <th className="px-3 py-2 font-medium">Etiqueta</th>
              <th className="px-3 py-2 font-medium">Estado</th>
              <th className="px-3 py-2 font-medium">Creada</th>
              <th className="px-3 py-2 font-medium">Vence</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {invitesQ.isLoading && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">Cargando…</td></tr>
            )}
            {invitesQ.isError && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-red-600">
                Error: {(invitesQ.error as Error).message}
              </td></tr>
            )}
            {invitesQ.data?.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">Sin invitaciones aún.</td></tr>
            )}
            {invitesQ.data?.map((inv) => {
              const b = STATUS_BADGE[inv.status];
              return (
                <tr key={inv.id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-1.5">{inv.email}</td>
                  <td className="px-3 py-1.5 text-slate-500">{inv.etiqueta ?? "—"}</td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full border ${b.cls}`}>{b.label}</span>
                  </td>
                  <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{fmtDate(inv.creado_en)}</td>
                  <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{fmtDate(inv.expira_en)}</td>
                  <td className="px-3 py-1.5 text-right">
                    {inv.status === "valid" && (
                      <button
                        type="button"
                        onClick={() => revocar(inv.id)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Cancelar
                      </button>
                    )}
                    {inv.submission_id && (
                      <button
                        type="button"
                        onClick={() => verDetalle(inv.submission_id!)}
                        className="text-xs text-sky-700 hover:underline"
                      >
                        Ver alta
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Altas recibidas */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b text-sm font-semibold text-slate-700">Altas recibidas</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Razón Social</th>
              <th className="px-3 py-2 font-medium">RFC</th>
              <th className="px-3 py-2 font-medium">Cuentas</th>
              <th className="px-3 py-2 font-medium">Recibida</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {subsQ.isLoading && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">Cargando…</td></tr>
            )}
            {subsQ.data?.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">Sin altas todavía.</td></tr>
            )}
            {subsQ.data?.map((s) => (
              <tr key={s.id} className="border-t hover:bg-slate-50">
                <td className="px-3 py-1.5">{s.razon_social}</td>
                <td className="px-3 py-1.5 font-mono text-xs">{s.rfc}</td>
                <td className="px-3 py-1.5">
                  {s.monedas ?? "—"}
                  {s.num_cuentas > 1 && (
                    <span className="text-slate-400 text-xs ml-1">({s.num_cuentas})</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{fmtDate(s.enviado_en)}</td>
                <td className="px-3 py-1.5 text-right">
                  <button
                    type="button"
                    onClick={() => verDetalle(s.id)}
                    className="text-xs text-sky-700 hover:underline"
                  >
                    Ver detalle
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(detail || detailLoading) && (
        <DetalleModal
          detail={detail}
          loading={detailLoading}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 text-right">{value ?? "—"}</span>
    </div>
  );
}

function DetalleModal({
  detail,
  loading,
  onClose,
}: {
  detail: RegistroSubmissionDetail | null;
  loading: boolean;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-semibold text-slate-800">
            {loading ? "Cargando…" : `Alta — ${detail?.razon_social}`}
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        {detail && (
          <div className="space-y-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Generales</div>
              <Row label="Razón Social" value={detail.razon_social} />
              <Row label="Nombre Comercial" value={detail.nombre_comercial} />
              <Row label="RFC" value={<span className="font-mono">{detail.rfc}</span>} />
              <Row label="Régimen Fiscal" value={detail.regimen_fiscal} />
              <Row label="Giro" value={detail.giro} />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Domicilio fiscal</div>
              <Row label="C.P." value={detail.cp} />
              <Row label="Vialidad" value={[detail.tipo_vialidad, detail.nombre_vialidad].filter(Boolean).join(" ") || null} />
              <Row label="No. Ext / Int" value={[detail.num_exterior, detail.num_interior].filter(Boolean).join(" / ") || null} />
              <Row label="Colonia" value={detail.colonia} />
              <Row label="Localidad" value={detail.localidad} />
              <Row label="Municipio / Demarcación" value={detail.municipio} />
              <Row label="Entidad Federativa" value={detail.entidad} />
              <Row label="Entre calle / y calle" value={[detail.entre_calle, detail.y_calle].filter(Boolean).join(" y ") || null} />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Contacto</div>
              <Row label="Nombre" value={detail.contacto_nombre} />
              <Row label="Cargo" value={detail.contacto_cargo} />
              <Row label="Correo" value={detail.contacto_correo} />
              <Row label="Teléfono" value={detail.contacto_telefono} />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">
                Cuentas bancarias ({detail.cuentas.length})
              </div>
              {detail.cuentas.length === 0 && (
                <div className="text-sm text-slate-400">Sin cuentas.</div>
              )}
              {detail.cuentas.map((c, i) => (
                <div key={i} className="mb-2 pb-1 border-b border-slate-100 last:border-0">
                  <div className="text-[11px] font-semibold text-sky-700 uppercase tracking-wide mb-0.5">
                    Cuenta {i + 1} · {c.moneda ?? "—"}
                  </div>
                  <Row label="Banco" value={c.banco} />
                  <Row label="Titular" value={c.titular} />
                  <Row label="Cuenta" value={c.cuenta ? <span className="font-mono">{c.cuenta}</span> : null} />
                  <Row label="CLABE" value={c.clabe ? <span className="font-mono">{c.clabe}</span> : null} />
                  <Row label="SWIFT" value={c.swift} />
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Documentos</div>
              <div className="flex flex-col gap-2 mt-1">
                {detail.csf_disponible ? (
                  <button type="button"
                     onClick={() => descargarArchivoRegistro(detail.id, "csf").catch((e) => alert((e as Error).message))}
                     className="inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline text-left">
                    <Download className="w-4 h-4" /> Constancia de Situación Fiscal
                  </button>
                ) : <span className="text-sm text-slate-400">CSF no disponible</span>}
                {detail.caratula_disponible ? (
                  <button type="button"
                     onClick={() => descargarArchivoRegistro(detail.id, "caratula").catch((e) => alert((e as Error).message))}
                     className="inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline text-left">
                    <Download className="w-4 h-4" /> Carátula bancaria
                  </button>
                ) : <span className="text-sm text-slate-400">Carátula no disponible</span>}
              </div>
            </div>
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Confirma la CLABE por un canal conocido (teléfono / carátula) antes de registrar al
              proveedor para pagos. Recibida el {fmtDate(detail.enviado_en)} desde {detail.ip ?? "IP desconocida"}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
